/**
 * MIME type utilities for human-friendly file type display
 */

/**
 * Map of MIME types to human-friendly display names
 */
const MIME_TYPE_DISPLAY_NAMES: Record<string, string> = {
  // Documents
  'application/pdf': 'PDF',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template':
    'Word Template',
  'application/rtf': 'RTF',
  'application/vnd.oasis.opendocument.text': 'ODT',
  'text/plain': 'Text',
  'text/html': 'HTML',

  // Spreadsheets
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template':
    'Excel Template',
  'text/csv': 'CSV',
  'application/vnd.oasis.opendocument.spreadsheet': 'ODS',

  // Presentations
  'application/vnd.ms-powerpoint': 'PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.template':
    'PowerPoint Template',
  'application/vnd.oasis.opendocument.presentation': 'ODP',

  // Images
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPEG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
  'image/svg+xml': 'SVG',
  'image/bmp': 'BMP',
  'image/tiff': 'TIFF',

  // Archives
  'application/zip': 'ZIP',
  'application/x-zip-compressed': 'ZIP',
  'application/x-rar-compressed': 'RAR',
  'application/x-7z-compressed': '7Z',
  'application/gzip': 'GZIP',
  'application/x-tar': 'TAR',

  // Audio/Video
  'audio/mpeg': 'MP3',
  'audio/wav': 'WAV',
  'video/mp4': 'MP4',
  'video/mpeg': 'MPEG',
  'video/quicktime': 'MOV',

  // Data formats
  'application/json': 'JSON',
  'application/xml': 'XML',
  'text/xml': 'XML',

  // Other common types
  'application/octet-stream': 'Binary',
}

/**
 * Map of file extensions to human-friendly display names (fallback)
 */
const EXTENSION_DISPLAY_NAMES: Record<string, string> = {
  pdf: 'PDF',
  doc: 'Word',
  docx: 'Word',
  dot: 'Word Template',
  dotx: 'Word Template',
  rtf: 'RTF',
  odt: 'ODT',
  txt: 'Text',
  html: 'HTML',
  htm: 'HTML',

  xls: 'Excel',
  xlsx: 'Excel',
  xlt: 'Excel Template',
  xltx: 'Excel Template',
  csv: 'CSV',
  ods: 'ODS',

  ppt: 'PowerPoint',
  pptx: 'PowerPoint',
  pot: 'PowerPoint Template',
  potx: 'PowerPoint Template',
  odp: 'ODP',

  jpg: 'JPEG',
  jpeg: 'JPEG',
  png: 'PNG',
  gif: 'GIF',
  webp: 'WebP',
  svg: 'SVG',
  bmp: 'BMP',
  tiff: 'TIFF',
  tif: 'TIFF',

  zip: 'ZIP',
  rar: 'RAR',
  '7z': '7Z',
  gz: 'GZIP',
  tar: 'TAR',

  mp3: 'MP3',
  wav: 'WAV',
  mp4: 'MP4',
  mpeg: 'MPEG',
  mov: 'MOV',

  json: 'JSON',
  xml: 'XML',
}

/**
 * Get a human-friendly display name for a file based on its MIME type and filename
 *
 * @param contentType - The MIME type of the file (e.g., 'application/pdf')
 * @param filename - The filename (used as fallback to extract extension)
 * @returns Human-friendly type name (e.g., 'PDF', 'Word', 'Excel')
 *
 * @example
 * getFileTypeDisplayName('application/pdf', 'document.pdf') // 'PDF'
 * getFileTypeDisplayName('application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'report.docx') // 'Word'
 * getFileTypeDisplayName(null, 'data.csv') // 'CSV'
 * getFileTypeDisplayName('application/octet-stream', 'unknown.xyz') // 'XYZ'
 */
export function getFileTypeDisplayName(
  contentType: string | null | undefined,
  filename: string | null | undefined,
): string {
  // Try MIME type lookup first
  if (contentType) {
    const displayName = MIME_TYPE_DISPLAY_NAMES[contentType.toLowerCase()]
    if (displayName) return displayName
  }

  // Fall back to extension lookup
  if (filename) {
    const extension = filename.split('.').pop()?.toLowerCase()
    if (extension) {
      const displayName = EXTENSION_DISPLAY_NAMES[extension]
      if (displayName) return displayName
      // Return uppercase extension as last resort
      return extension.toUpperCase()
    }
  }

  // Ultimate fallback
  return 'FILE'
}
